<template>
  <div class="pv">
    <div class="pv-toolbar">
      <div class="pv-toolbar-info">
        Arraste pelo <span class="pv-grip-inline">⠿</span> para reordenar campos (dentro e entre
        seções). Use as setas <strong>▲▼</strong> para reordenar as seções e edite o nome no
        título. O preview reflete visibilidade, ordem e rótulos — é só uma prévia aproximada, então
        a tela final que o operador usa pode ter pequenas diferenças visuais.
      </div>
      <div class="pv-toolbar-acoes">
        <!-- Layout por MODELO: posiciona os campos na ordem/seção daquele modelo (só posição;
             visibilidade/regras vêm de "Campos do sistema"). 1 modelo → botão; vários → select. -->
        <CButton
          v-if="presets.length === 1"
          size="sm"
          color="primary"
          variant="outline"
          class="pv-layout-padrao"
          title="Posiciona os campos na ordem e seção padrão. Respeita a visibilidade e as regras de Campos do sistema."
          @click="confirmarLayout(presets[0])"
        >
          <fa-icon icon="sync" /> Aplicar layout padrão
        </CButton>
        <CDropdown
          v-else-if="presets.length > 1"
          size="sm"
          placement="bottom-start"
          add-toggler-classes="btn-outline-primary pv-layout-padrao"
        >
          <template #toggler-content>
            <fa-icon icon="sync" /> Aplicar layout padrão
          </template>
          <CDropdownItem v-for="p in presets" :key="p.key" @click="confirmarLayout(p)">
            {{ p.nome }}
          </CDropdownItem>
        </CDropdown>
        <div class="pv-devices">
          <button
            v-for="d in devices"
            :key="d.key"
            type="button"
            :class="['pv-dev-btn', { active: device === d.key }]"
            @click="device = d.key"
          >
            {{ d.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Pré-visualizar com regras: simples = aplicar um MODELO (chips de 1 clique); -->
    <!-- avançado = informar cada gatilho à mão. -->
    <div v-if="gatilhos.length" class="pv-sim">
      <div class="pv-sim-top">
        <span class="pv-sim-titulo"><fa-icon icon="filter" /> Pré-visualizar com regra</span>

        <div v-if="modelosFiltro.length" class="pv-sim-chips">
          <button
            v-for="m in chipsVisiveis"
            :key="m.id"
            type="button"
            class="pv-chip"
            :class="{ 'pv-chip--on': modeloAtivoId === m.id }"
            :title="m.nome"
            @click="toggleModeloFiltro(m)"
          >
            {{ rotuloModelo(m) }}
          </button>
          <button
            v-if="chipsOcultos > 0"
            type="button"
            class="pv-chip pv-chip-mais"
            @click="verTodosModelos = true"
          >
            +{{ chipsOcultos }}
          </button>
          <button
            v-else-if="verTodosModelos && modelosFiltro.length > limiteChips"
            type="button"
            class="pv-chip pv-chip-mais"
            @click="verTodosModelos = false"
          >
            ver menos
          </button>
        </div>
        <span v-else class="pv-sim-dica">crie modelos de regra para pré-visualizar em 1 clique</span>

        <button type="button" class="pv-sim-avancado" @click="modoAvancado = !modoAvancado">
          <fa-icon :icon="modoAvancado ? 'chevron-up' : 'sliders-h'" />
          avançado
        </button>
      </div>

      <!-- Estado: o que está sendo simulado, em linguagem natural -->
      <div v-if="filtroAtivo" class="pv-sim-estado">
        <fa-icon icon="eye" />
        <span>Pré-visualizando: <strong>{{ descricaoSimulacao }}</strong></span>
        <button type="button" class="pv-sim-limpar" @click="limparFiltro">
          <fa-icon icon="times" /> limpar
        </button>
      </div>

      <!-- Modo avançado: um campo por gatilho (grid uniforme) -->
      <div v-if="modoAvancado" class="pv-sim-grid">
        <label v-for="g in gatilhos" :key="g.campo" class="pv-filtro-item">
          <span class="pv-filtro-label">{{ g.label }}</span>
          <select
            v-if="g.campo === 'tipo_pessoa'"
            class="form-control"
            :value="filtroValores[g.campo] || ''"
            @change="(e) => setFiltro(g.campo, e.target.value)"
          >
            <option value="">—</option>
            <option value="PF">Pessoa Física</option>
            <option value="PJ">Pessoa Jurídica</option>
          </select>
          <input
            v-else
            class="form-control"
            :value="filtroValores[g.campo] || ''"
            placeholder="valor"
            @input="(e) => setFiltro(g.campo, e.target.value)"
          />
        </label>
      </div>
    </div>

    <div class="pv-stage">
      <div class="pv-frame" :class="{ 'pv-frame--sim': filtroAtivo }" :style="{ maxWidth: larguraAtual }">
        <div v-if="filtroAtivo" class="pv-sim-faixa">
          <fa-icon icon="eye" /> pré-visualização simulada
        </div>

        <div v-if="!grupos.length" class="pv-vazio">
          Nenhuma seção configurada.
        </div>

        <div>
          <div v-for="(secao, idx) in grupos" :key="secao.chave" class="pv-secao">
            <div class="pv-secao-head">
              <span class="pv-secao-pos" title="Ordem da seção">{{ idx + 1 }}</span>
              <span class="pv-secao-mover">
                <button
                  type="button"
                  class="pv-mover-btn"
                  :disabled="idx === 0"
                  title="Mover seção para cima"
                  @click="$emit('mover-secao', { chave: secao.chave, direcao: 'cima' })"
                >▲</button>
                <button
                  type="button"
                  class="pv-mover-btn"
                  :disabled="idx === grupos.length - 1"
                  title="Mover seção para baixo"
                  @click="$emit('mover-secao', { chave: secao.chave, direcao: 'baixo' })"
                >▼</button>
              </span>
              <input
                class="pv-secao-input"
                v-model="secao.titulo"
                maxlength="40"
                title="Nome da seção exibido ao operador"
                @change="$emit('renomear-secao', { chave: secao.chave, label: secao.titulo })"
              />
              <!-- Nome SIMULADO da seção (efeito `rotular`) quando há simulação ativa -->
              <span
                v-if="filtroAtivo && secaoSimLabel(secao) !== secao.titulo"
                class="pv-secao-sim"
                :title="'Nome simulado: ' + secaoSimLabel(secao)"
              >→ {{ secaoSimLabel(secao) }}</span>
              <span
                v-if="!secao.custom"
                class="pv-secao-sistema"
                v-c-tooltip="{
                  content:
                    'Seção padrão do sistema: você pode renomear e reordenar, mas não remover.',
                  placement: 'top',
                }"
              >
                <fa-icon icon="lock" /> sistema
              </span>
              <!-- Regra (rotular) da seção: renomear o nome da seção por condição -->
              <CButton
                size="sm"
                :color="regrasDaSecao(secao.chave).length ? 'primary' : 'info'"
                variant="ghost"
                class="pv-secao-regra"
                :title="'Renomear a seção por condição (ex.: PF → Dados Pessoais)'"
                @click="toggleRegrasSecao(secao)"
              >
                <fa-icon icon="list-alt" />
                {{ regrasDaSecao(secao.chave).length ? `${regrasDaSecao(secao.chave).length} regra(s)` : 'Regra' }}
              </CButton>
              <CButton
                v-if="secao.custom"
                size="sm"
                color="danger"
                variant="ghost"
                class="pv-secao-remover"
                :disabled="grupos.length <= 1"
                title="Remover seção"
                @click="removerSecao(secao)"
              >
                <fa-icon icon="times" />
              </CButton>
            </div>

            <!-- Builder de regra da seção (só efeito `rotular`) -->
            <div v-if="secaoExpandida === secao.chave" class="pv-secao-regra-wrap">
              <RegraCondicionalBuilder
                :campo="secaoCampoExpandida"
                :gatilhos="gatilhos"
                :modelos="modelos"
                :somente-rotular="true"
                @input="(regras) => onSecaoRegras(secao.chave, regras)"
                @salvar-modelo="(regra) => $emit('salvar-modelo', regra)"
              />
            </div>

            <draggable
              v-model="secao.campos"
              group="campos-preview"
              handle=".pv-drag"
              :animation="150"
              ghost-class="pv-ghost"
              chosen-class="pv-chosen"
              drag-class="pv-dragging"
              class="pv-campos"
              :move="aoMover"
              @end="aoReordenar"
            >
              <div
                v-for="campo in secao.campos"
                :key="campo.campo"
                :class="['pv-campo', { 'pv-campo--full': campoCheio(campo) }]"
              >
                <label class="pv-label">
                  <span
                    v-if="!campo.fixarPrimeiro"
                    class="pv-drag"
                    title="Arrastar para reordenar"
                  >⠿</span>
                  <span
                    v-else
                    class="pv-fixo"
                    v-c-tooltip="{
                      content: 'Campo fixo: é sempre o primeiro',
                      placement: 'top',
                    }"
                  >📌</span>
                  {{ campoLabel(campo) }}
                  <span v-if="campoObrigatorio(campo)" class="pv-req">*</span>
                  <fa-icon
                    v-if="(campo.regras || []).length"
                    icon="list-alt"
                    class="pv-regras-info"
                    v-c-tooltip="{ content: textoRegras(campo), placement: 'top' }"
                  />
                </label>

                <div :class="['pv-control', 'pv-control--' + tipoControle(campo)]">
                  <span v-if="tipoControle(campo) === 'checkbox'" class="pv-check" />
                  <span class="pv-control-text">{{ placeholder(campo) }}</span>
                  <span
                    v-if="['select', 'data'].includes(tipoControle(campo))"
                    class="pv-chevron"
                  >▾</span>
                </div>

                <!-- Campos VINCULADOS (ex.: isento → IE): adorno do campo, não linha própria. -->
                <label
                  v-for="vc in vinculadosDe(campo)"
                  :key="vc.campo"
                  class="pv-vinculo"
                >
                  <span class="pv-check" /> {{ campoLabel(vc) }}
                </label>
              </div>
            </draggable>

            <div v-if="!secao.campos.length" class="pv-secao-vazia">
              Arraste campos para esta seção.
            </div>
          </div>
        </div>

        <div class="pv-add-secao">
          <CButton color="primary" variant="outline" @click="adicionarSecao">
            <fa-icon icon="plus" /> Adicionar seção
          </CButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { avaliarCampos, avaliarSecoes } from '../helpers/regrasCampoCadastro';
import { formatarRegras, formatarCondicoes, modeloCompativel } from '../helpers/formatarRegra';
import RegraCondicionalBuilder from './RegraCondicionalBuilder.vue';

const SECAO_LABELS = {
  dados: 'Dados da Empresa',
  contatos: 'Contatos',
  enderecos: 'Endereços',
};
const SECAO_ORDEM = ['dados', 'contatos', 'enderecos'];

export default {
  name: 'PreviewLayout',
  components: { draggable, RegraCondicionalBuilder },
  props: {
    campos: { type: Array, default: () => [] },
    secoes: { type: Array, default: () => [] },
    gatilhos: { type: Array, default: () => [] },
    modelos: { type: Array, default: () => [] },
    // modelos de layout (presets). 1 = botão único (padrão único, ex.: revendedor);
    // vários = select (ex.: cliente). Cada modelo traz sua própria disposição (ordem/seção).
    presets: { type: Array, default: () => [] },
  },
  data() {
    return {
      device: 'desktop',
      devices: [
        { key: 'mobile', label: 'Mobile' },
        { key: 'tablet', label: 'Tablet' },
        { key: 'desktop', label: 'Desktop' },
      ],
      // valores simulados por gatilho (filtro de regra do preview)
      filtroValores: {},
      // id do modelo aplicado como filtro (null = cenário manual/avançado)
      modeloAtivoId: null,
      // modo avançado = informar cada gatilho à mão (o simples usa só modelos)
      modoAvancado: false,
      // controle de "muitos modelos": mostra só 2 chips + "+N" para expandir
      verTodosModelos: false,
      limiteChips: 2,
      // seção com o builder de regra (rotular) aberto + pseudo-"campo" estável p/ o builder.
      secaoExpandida: null,
      secaoCampoExpandida: null,
      // grupos = lista MUTÁVEL por seção (vuedraggable). Os objetos de campo são as
      // MESMAS referências de `campos` (localCampos no pai) → mexer em ordem/seção reflete
      // no Salvar. As alterações estruturais de seção sobem por evento ao pai (localSecoes).
      grupos: [],
    };
  },
  computed: {
    larguraAtual() {
      return { mobile: '320px', tablet: '768px', desktop: '100%' }[this.device];
    },
    filtroAtivo() {
      return Object.values(this.filtroValores).some((v) => v !== '' && v != null);
    },
    // Remapeia os valores PLANOS do filtro para a estrutura que o avaliador lê: gatilhos de
    // fonte `receita` aninham em .receita[chave]; os derivados de CNAE (cnae_qualquer/
    // cnae_principal) alimentam .cnae. Sem isto o preview NÃO casava o caso-âncora da feature
    // ("IE obrigatória quando CNAE ∈ lista") nem regras de situação na Receita.
    valoresAvaliacao() {
      const out = {};
      Object.entries(this.filtroValores).forEach(([chave, valor]) => {
        if (valor === '' || valor == null) return;
        const g = (this.gatilhos || []).find((x) => x.campo === chave);
        const fonte = (g && g.fonte) || 'campo';
        if (fonte === 'receita') {
          out.receita = { ...(out.receita || {}), [chave]: valor };
        } else if (chave === 'cnae_qualquer' || chave === 'cnae_principal') {
          out.cnae = valor;
        } else {
          out[chave] = valor;
        }
      });
      return out;
    },
    // overlay {campo: {visivel, obrigatorio, label}} quando o filtro está ativo; senão null.
    overlay() {
      if (!this.filtroAtivo) return null;
      return avaliarCampos(this.campos, this.valoresAvaliacao);
    },
    // overlay {secao: {label}} das seções quando o filtro está ativo (efeito `rotular`).
    secaoOverlay() {
      if (!this.filtroAtivo) return null;
      return avaliarSecoes(this.secoes, this.valoresAvaliacao);
    },
    // modelos compatíveis com os gatilhos desta entidade (para "modelo como filtro").
    modelosCompat() {
      return (this.modelos || []).filter((m) => modeloCompativel(m, this.gatilhos));
    },
    // chips de filtro = modelos compatíveis SEM repetir condição (no filtro só importa
    // o cenário; modelos com a mesma condição viram o mesmo filtro).
    modelosFiltro() {
      const vistos = new Set();
      return this.modelosCompat.filter((m) => {
        const chave = this.rotuloModelo(m);
        if (vistos.has(chave)) return false;
        vistos.add(chave);
        return true;
      });
    },
    // chips realmente exibidos (limita a `limiteChips` salvo se "ver todos").
    chipsVisiveis() {
      return this.verTodosModelos ? this.modelosFiltro : this.modelosFiltro.slice(0, this.limiteChips);
    },
    chipsOcultos() {
      return this.verTodosModelos ? 0 : Math.max(0, this.modelosFiltro.length - this.limiteChips);
    },
    // descrição do CENÁRIO simulado (só as condições, coerente com o avançado).
    descricaoSimulacao() {
      if (this.modeloAtivoId) {
        const m = (this.modelos || []).find((x) => x.id === this.modeloAtivoId);
        if (m) return formatarCondicoes(m.condicoes, this.gatilhos, m.conector);
      }
      // cenário manual (avançado): trata cada valor como uma condição "igual a".
      const condicoes = Object.entries(this.filtroValores)
        .filter(([, v]) => v !== '' && v != null)
        .map(([chave, valor]) => ({ chave, operador: 'igual', valor }));
      return condicoes.length
        ? formatarCondicoes(condicoes, this.gatilhos, 'E')
        : 'cenário personalizado';
    },
  },
  watch: {
    // re-sincroniza ao mudar visibilidade/label/ordem na aba "Campos do sistema".
    campos: {
      deep: true,
      immediate: true,
      handler() {
        this.grupos = this.montarGrupos();
      },
    },
    // re-sincroniza ao renomear/reordenar/criar/remover seções.
    secoes: {
      deep: true,
      handler() {
        this.grupos = this.montarGrupos();
      },
    },
    // re-renderiza ao simular gatilhos (filtro de regra).
    filtroValores: {
      deep: true,
      handler() {
        this.grupos = this.montarGrupos();
      },
    },
  },
  methods: {
    // visibilidade/obrigatoriedade considerando o overlay do filtro (se ativo).
    campoVisivel(campo) {
      return this.overlay ? !!(this.overlay[campo.campo] || {}).visivel : !!campo.visivel;
    },
    campoObrigatorio(campo) {
      return this.overlay ? !!(this.overlay[campo.campo] || {}).obrigatorio : !!campo.obrigatorio;
    },
    textoRegras(campo) {
      return formatarRegras(campo.regras || [], this.gatilhos).join(' • ');
    },
    // rótulo do campo considerando o efeito `rotular` quando há simulação ativa.
    campoLabel(campo) {
      const base = campo.label || campo.campo;
      if (!this.overlay) return base;
      return (this.overlay[campo.campo] || {}).label || base;
    },
    // nome simulado da seção (efeito `rotular`) — usado como dica ao lado do título.
    secaoSimLabel(secao) {
      if (!this.secaoOverlay) return secao.titulo;
      return (this.secaoOverlay[secao.chave] || {}).label || secao.titulo;
    },
    regrasDaSecao(chave) {
      const s = (this.secoes || []).find((x) => x.secao === chave);
      return (s && s.regras) || [];
    },
    // abre/fecha o builder de regra da seção; mantém um pseudo-"campo" estável (a ref do
    // builder não pode mudar a cada render, senão reinicia o estado interno enquanto edita).
    toggleRegrasSecao(secao) {
      if (this.secaoExpandida === secao.chave) {
        this.secaoExpandida = null;
        this.secaoCampoExpandida = null;
        return;
      }
      this.secaoExpandida = secao.chave;
      this.secaoCampoExpandida = {
        label: secao.titulo,
        campo: secao.chave,
        regras: this.regrasDaSecao(secao.chave),
      };
    },
    onSecaoRegras(chave, regras) {
      this.$emit('editar-regras-secao', { chave, regras });
    },
    // "Aplicar layout padrão" (por modelo): posiciona os campos na ordem/seção daquele modelo.
    // NÃO altera visibilidade/obrigatório/regras — respeita o que está em "Campos do sistema".
    confirmarLayout(preset) {
      this.$swal({
        title: `Aplicar layout padrão${preset && preset.nome ? ` "${preset.nome}"` : ''}?`,
        text: 'Posiciona os campos na ordem e seção deste modelo. Não altera visibilidade, obrigatoriedade ou regras (respeita o que está em Campos do sistema). Você ainda precisa clicar em Salvar.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Aplicar',
        cancelButtonText: 'Cancelar',
      }).then((r) => {
        if (r.value) this.$emit('aplicar-layout-padrao', preset);
      });
    },
    // rótulo do chip de filtro = só a CONDIÇÃO do modelo (sem a ação).
    rotuloModelo(m) {
      return formatarCondicoes(m.condicoes, this.gatilhos, m.conector);
    },
    setFiltro(chave, valor) {
      // edição manual descola de qualquer modelo aplicado (vira cenário personalizado)
      this.modeloAtivoId = null;
      this.$set(this.filtroValores, chave, valor);
    },
    // aplica/desaplica um modelo como filtro (toggle); o chip ativo fica destacado.
    toggleModeloFiltro(modelo) {
      if (this.modeloAtivoId === modelo.id) {
        this.limparFiltro();
        return;
      }
      const valores = {};
      (modelo.condicoes || []).forEach((c) => {
        const valor = Array.isArray(c.valor) ? c.valor[0] : c.valor;
        valores[c.chave] = valor == null ? '' : String(valor);
      });
      this.filtroValores = valores;
      this.modeloAtivoId = modelo.id;
    },
    limparFiltro() {
      this.filtroValores = {};
      this.modeloAtivoId = null;
    },
    // Campos VINCULADOS (ex.: isento → IE) visíveis cujo alvo é `campo` — renderizados
    // como adorno DENTRO do alvo, nunca como linha própria no Layout.
    vinculadosDe(campo) {
      return (this.campos || []).filter(
        (c) => c.vinculadoA === campo.campo && this.campoVisivel(c)
      );
    },
    montarGrupos() {
      // Exclui os vinculados das linhas ordenáveis: viram adorno do alvo (vinculadosDe).
      const visiveis = (this.campos || [])
        .filter((c) => this.campoVisivel(c) && !c.vinculadoA)
        .slice()
        .sort((a, b) => (a.ordem ?? 999) - (b.ordem ?? 999));

      // campos visíveis agrupados por seção
      const porSecao = {};
      visiveis.forEach((c) => {
        const s = c.secao || 'outros';
        (porSecao[s] = porSecao[s] || []).push(c);
      });

      // Seções autoritativas = a config resolvida (prop secoes). Renderiza TODAS (inclusive
      // vazias) para servirem de alvo de drag/CRUD. + fallback p/ seção referenciada por
      // campo que não esteja na lista (defensivo) e p/ backend antigo sem `secoes`.
      const lista = (this.secoes || []).slice();
      const conhecidas = new Set(lista.map((s) => s.secao));
      Object.keys(porSecao).forEach((s) => {
        if (!conhecidas.has(s)) {
          conhecidas.add(s);
          lista.push({ secao: s, label: SECAO_LABELS[s] || s, ordem: null, custom: false });
        }
      });

      const ordemDe = (s) => {
        if (s.ordem != null) return s.ordem;
        const i = SECAO_ORDEM.indexOf(s.secao);
        return i === -1 ? 9999 : i;
      };

      return lista
        .slice()
        .sort((a, b) => ordemDe(a) - ordemDe(b))
        .map((s) => ({
          chave: s.secao,
          titulo: s.label || SECAO_LABELS[s.secao] || s.secao,
          custom: !!s.custom,
          campos: porSecao[s.secao] || [],
        }));
    },
    aoMover(evt) {
      // Campo `fixarPrimeiro` (documento, vinculado ao botão Importar Receita) é âncora:
      // não pode ser arrastado, nem aceitar outro campo solto antes dele (índice 0 da seção).
      const arrastado = evt.draggedContext && evt.draggedContext.element;
      if (arrastado && arrastado.fixarPrimeiro) return false;
      const destino = (evt.relatedContext && evt.relatedContext.list) || [];
      const temFixo = destino.some((c) => c && c.fixarPrimeiro);
      if (temFixo && evt.draggedContext && evt.draggedContext.futureIndex === 0) {
        return false;
      }
      return true;
    },
    aoReordenar() {
      // renumera os campos VISÍVEIS pela ordem de exibição atual (passo 10 deixa folga)
      // e fixa a seção de cada campo na do grupo onde ele está agora (move entre seções).
      // refs compartilhadas com localCampos → entra no delta do Salvar (campo só muda no
      // delta se a seção/ordem realmente mudou).
      let i = 0;
      this.grupos.forEach((secao) => {
        secao.campos.forEach((campo) => {
          campo.ordem = (i += 1) * 10;
          campo.secao = secao.chave;
        });
      });
    },
    // reordenar seções é via setas (emite 'mover-secao'); o pai renumera a ordem.
    adicionarSecao() {
      this.$swal({
        title: 'Nova seção',
        input: 'text',
        inputLabel: 'Nome da seção',
        inputPlaceholder: 'Ex.: Dados Fiscais',
        showCancelButton: true,
        confirmButtonText: 'Adicionar',
        cancelButtonText: 'Cancelar',
        inputValidator: (v) => (!v || !v.trim() ? 'Informe um nome para a seção.' : undefined),
      }).then((r) => {
        if (r.value) this.$emit('adicionar-secao', { label: r.value.trim() });
      });
    },
    removerSecao(secao) {
      this.$swal({
        title: `Remover a seção "${secao.titulo}"?`,
        text: 'Os campos desta seção serão movidos para a primeira seção. Lembre de Salvar.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remover',
        cancelButtonText: 'Cancelar',
      }).then((r) => {
        if (r.value) this.$emit('remover-secao', { chave: secao.chave });
      });
    },
    tipoControle(campo) {
      const t = campo.tipo;
      if (['composto', 'upload'].includes(t)) return 'bloco';
      if (t === 'checkbox') return 'checkbox';
      if (t === 'radio') return 'radio';
      if (t === 'textarea') return 'textarea';
      if (['select_remoto', 'select'].includes(t)) return 'select';
      if (t === 'data') return 'data';
      return 'texto';
    },
    campoCheio(campo) {
      return ['textarea', 'composto', 'upload'].includes(campo.tipo);
    },
    placeholder(campo) {
      const ctrl = this.tipoControle(campo);
      if (ctrl === 'bloco') {
        return campo.tipo === 'upload' ? 'Anexar arquivos' : campo.label || campo.campo;
      }
      if (ctrl === 'checkbox') return campo.label || campo.campo;
      if (ctrl === 'radio') return 'Não / Sim';
      if (ctrl === 'select') return 'Selecione';
      if (ctrl === 'data') return 'dd/mm/aaaa';
      return '';
    },
  },
};
</script>

<style scoped>
.pv-toolbar {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  margin-bottom: 16px; flex-wrap: wrap;
}
.pv-toolbar-info { font-size: 12px; color: #667085; max-width: 60%; }
.pv-grip-inline { color: #98a2b3; }
.pv-toolbar-acoes { display: inline-flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pv-layout-padrao { font-size: 12px; white-space: nowrap; }
.pv-devices { display: inline-flex; border: 1px solid #d0d5dd; border-radius: 8px; overflow: hidden; }
.pv-dev-btn {
  border: 0; background: #fff; color: #475467; font-size: 12px; font-weight: 600;
  padding: 7px 14px; cursor: pointer; border-right: 1px solid #eaecf0;
}
.pv-dev-btn:last-child { border-right: 0; }
.pv-dev-btn.active { background: #3a3e81; color: #fff; }

.pv-stage {
  background: #f2f4f7; border-radius: 10px; padding: 24px 16px;
  display: flex; justify-content: center; min-height: 240px;
}
.pv-frame {
  width: 100%; background: #fff; border: 1px solid #e4e7eb; border-radius: 10px;
  padding: 18px; transition: max-width 0.2s ease; box-shadow: 0 1px 3px rgba(16,24,40,0.06);
}
.pv-vazio { text-align: center; color: #98a2b3; font-size: 13px; padding: 32px; }

.pv-secao { margin-bottom: 18px; }
.pv-secao:last-child { margin-bottom: 0; }
.pv-secao-head {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 10px; border-bottom: 1px solid #eef0f4; padding-bottom: 5px;
}
.pv-secao-pos {
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 22px; height: 22px; border-radius: 50%;
  background: #eef0f4; color: #3a3e81; font-size: 11px; font-weight: 700;
}
.pv-secao-mover { display: inline-flex; flex-direction: column; gap: 2px; flex-shrink: 0; }
.pv-mover-btn {
  border: 1px solid #d0d5dd; background: #fff; color: #475467;
  border-radius: 4px; width: 22px; height: 14px; padding: 0; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 8px; line-height: 1;
}
.pv-mover-btn:hover:not(:disabled) { border-color: #3a3e81; color: #3a3e81; }
.pv-mover-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.pv-secao-input {
  border: 1px solid transparent; background: transparent; border-radius: 6px;
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;
  color: #3a3e81; font-weight: 700; padding: 3px 6px; flex: 1; min-width: 0;
}
.pv-secao-input:hover { border-color: #e4e7eb; }
.pv-secao-input:focus { outline: none; border-color: #3a3e81; background: #fff; text-transform: none; }
.pv-secao-sistema {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.02em;
  color: #3538cd; background: #eef4ff; border: 1px solid #d1e0ff;
  border-radius: 10px; padding: 1px 8px; cursor: help; flex-shrink: 0;
}
.pv-secao-remover { flex-shrink: 0; }
.pv-secao-regra { flex-shrink: 0; font-size: 11px; }
.pv-secao-sim {
  display: inline-flex; align-items: center; font-size: 11px; font-weight: 700;
  color: #3538cd; background: #eef4ff; border: 1px solid #d1e0ff;
  border-radius: 10px; padding: 1px 8px; flex-shrink: 0; white-space: nowrap;
}
.pv-secao-regra-wrap {
  border: 1px dashed #d1e0ff; border-radius: 8px; background: #fafbff;
  margin: -4px 0 12px; padding: 0 8px;
}
.pv-secao-vazia {
  font-size: 12px; color: #98a2b3; border: 1px dashed #d0d5dd; border-radius: 8px;
  padding: 12px; text-align: center; background: #fafbfc;
}

.pv-add-secao { margin-top: 10px; display: flex; justify-content: flex-end; }

.pv-campos { display: flex; flex-wrap: wrap; gap: 12px; min-height: 8px; }
.pv-campo { flex: 1 1 calc(50% - 6px); min-width: 180px; }
.pv-campo--full { flex-basis: 100%; }
.pv-label { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #344054; font-weight: 600; margin-bottom: 4px; }
.pv-req { color: #d92d20; }
.pv-regras-info { color: #3538cd; font-size: 11px; margin-left: 4px; cursor: help; }

/* Pré-visualização com regras (simulação) — limpo e coerente com o editor */
.pv-sim {
  background: #fff; border: 1px solid #e4e7eb; border-radius: 8px;
  padding: 10px 12px; margin-bottom: 14px;
}
.pv-sim-top { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 10px; }
.pv-sim-titulo { font-size: 12px; font-weight: 600; color: #344054; display: inline-flex; align-items: center; gap: 6px; }
.pv-sim-titulo .fa-icon, .pv-sim-titulo svg { color: #98a2b3; }
.pv-sim-dica { font-size: 11px; color: #98a2b3; }
.pv-sim-chips {
  display: flex; flex-wrap: wrap; gap: 6px; flex: 1;
  max-height: 120px; overflow-y: auto; /* não empilha infinito quando há muitos modelos */
}
.pv-chip {
  border: 1px solid #d0d5dd; background: #fff; color: #475467; cursor: pointer;
  font-size: 12px; border-radius: 999px; padding: 4px 12px; max-width: 240px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; transition: all 0.12s;
}
.pv-chip:hover { border-color: #3538cd; color: #3538cd; background: #fff; }
.pv-chip--on { background: #3538cd; border-color: #3538cd; color: #fff; }
/* hover do selecionado: escurece e MANTÉM o texto branco (evita azul-sobre-azul) */
.pv-chip--on:hover { background: #2b2ea6; border-color: #2b2ea6; color: #fff; }
.pv-chip-mais { color: #3538cd; border-style: dashed; max-width: none; }
.pv-chip-mais:hover { background: #eef4ff; }
.pv-sim-avancado {
  margin-left: auto; border: 0; background: transparent; color: #667085; font-size: 12px;
  cursor: pointer; display: inline-flex; align-items: center; gap: 5px;
}
.pv-sim-avancado:hover { color: #3538cd; }

/* Linha de estado: o que está sendo pré-visualizado */
.pv-sim-estado {
  display: flex; align-items: center; gap: 8px; margin-top: 10px;
  font-size: 12px; color: #3538cd; background: #eef4ff; border: 1px solid #d1e0ff;
  border-radius: 6px; padding: 6px 10px;
}
.pv-sim-estado strong { font-weight: 700; }
.pv-sim-limpar {
  margin-left: auto; border: 0; background: transparent; color: #d92d20; font-size: 12px;
  cursor: pointer; display: inline-flex; align-items: center; gap: 4px;
}

/* Grid do modo avançado */
.pv-sim-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 12px;
  margin-top: 10px; padding-top: 10px; border-top: 1px dashed #e4e7eb;
}
.pv-filtro-item { display: flex; flex-direction: column; gap: 3px; margin: 0; min-width: 0; }
.pv-filtro-label { font-size: 11px; color: #475467; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pv-filtro-item .form-control { height: 32px; font-size: 12px; width: 100%; }

/* Moldura do preview destacada quando há simulação ativa */
.pv-frame--sim { border-color: #3538cd; border-style: dashed; box-shadow: 0 0 0 3px rgba(53, 56, 205, 0.08); }
.pv-sim-faixa {
  display: inline-flex; align-items: center; gap: 6px; margin: -6px -6px 12px;
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: #3538cd; background: #eef4ff; border-radius: 6px; padding: 4px 10px; width: fit-content;
}
.pv-drag { cursor: grab; color: #b0b7c3; font-size: 13px; user-select: none; }
.pv-drag:active { cursor: grabbing; }
.pv-fixo { cursor: default; font-size: 12px; user-select: none; opacity: 0.7; }

/* feedback de arraste: o "fantasma" mostra o buraco onde o campo vai cair (move, não sobrepõe) */
.pv-ghost { opacity: 0.6; }
.pv-ghost .pv-control { border: 1px dashed #3a3e81; background: #eef4ff; }
.pv-ghost .pv-label { color: #3a3e81; }
.pv-chosen { cursor: grabbing; }
.pv-dragging {
  opacity: 0.97; background: #fff; border-radius: 8px;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.18);
}

.pv-control {
  display: flex; align-items: center; gap: 8px;
  height: 34px; border: 1px solid #d0d5dd; border-radius: 6px;
  padding: 0 10px; background: #fff; color: #98a2b3; font-size: 13px;
}
.pv-control--textarea { height: 64px; align-items: flex-start; padding-top: 8px; }
.pv-control--bloco { height: auto; min-height: 44px; border-style: dashed; color: #667085; }
.pv-control--checkbox { border: 0; padding: 0; height: auto; color: #344054; }
.pv-check {
  width: 16px; height: 16px; border: 1px solid #d0d5dd; border-radius: 4px; background: #fff; flex-shrink: 0;
}
.pv-control-text { flex: 1; }
.pv-chevron { color: #98a2b3; font-size: 11px; }
/* Campo vinculado (ex.: isento → IE): adorno renderizado sob o controle do alvo. */
.pv-vinculo { display: flex; align-items: center; gap: 6px; margin-top: 6px; font-size: 12px; color: #475467; font-weight: 500; }
</style>
